// Wiki page image upload: the same presign -> PUT -> confirm pipeline as
// coverUpload.ts, but for images inserted inline into a wiki page's markdown
// body rather than a project's cover slot. Returns just the confirmed URL -
// a wiki page embeds it as a markdown image reference (![alt](url)), not an
// engine asset record.
import { apiPost, apiUpload } from '../services/api';
import { AssetValidationError } from './glbUpload';

// Matches the api's ALLOWED_CONTENT_TYPES exactly (security.md): png/jpeg/webp
// at 2 MiB each are the only image types the presign endpoint will accept.
export const MAX_WIKI_IMAGE_BYTES = 2 * 1024 * 1024;

const WIKI_IMAGE_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

interface PresignWire {
  upload_url: string;
  asset_id: string;
  s3_key: string;
  cf_url: string;
}

interface ConfirmedAssetWire {
  cf_url: string;
}

export async function uploadWikiImage(projectId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const contentType = WIKI_IMAGE_TYPES[ext];
  if (!contentType) {
    throw new AssetValidationError('Images are .png, .jpg or .webp.');
  }
  if (file.size > MAX_WIKI_IMAGE_BYTES) {
    throw new AssetValidationError(
      `Image is ${(file.size / 1024 / 1024).toFixed(1)} MiB; the limit is 2 MiB.`,
    );
  }

  const presign = await apiPost<PresignWire>(`/projects/${projectId}/assets/presign`, {
    name: file.name,
    file_size: file.size,
    content_type: contentType,
  });
  await apiUpload(presign.upload_url, file, contentType);
  const confirmed = await apiPost<ConfirmedAssetWire>(`/projects/${projectId}/assets/confirm`, {
    asset_id: presign.asset_id,
    name: file.name.replace(/\.[^.]+$/, '') || 'wiki-image',
    file_size: file.size,
    content_type: contentType,
    s3_key: presign.s3_key,
    cf_url: presign.cf_url,
    meta: { wiki: true },
  });

  return confirmed.cf_url;
}
